'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Home } from '@/components/ui/icons'

interface BreadcrumbItem {
  label: string
  path: string
  current?: boolean
}

export function Breadcrumbs() {
  const pathname = usePathname()

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = pathname.split('/').filter(Boolean)

    if (pathSegments.length === 0) {
      return [{ label: 'Home', path: '/', current: true }]
    }

    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Home', path: '/' }]

    let currentPath = ''
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`
      const isLast = index === pathSegments.length - 1

      // Convert segment to readable label
      const label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

      breadcrumbs.push({
        label,
        path: currentPath,
        current: isLast,
      })
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  if (breadcrumbs.length <= 1) {
    return null
  }

  return (
    <nav aria-label="Breadcrumb" className="px-6 py-4 md:px-8">
      <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-xl">
        <ol className="flex items-center gap-2 text-xs font-medium md:text-sm">
          {breadcrumbs.map((breadcrumb, index) => (
            <li key={breadcrumb.path} className="flex items-center">
              {index > 0 && <span className="mx-1.5 text-white/20">/</span>}

              {breadcrumb.current ? (
                <span className="text-white" aria-current="page">
                  {breadcrumb.label}
                </span>
              ) : (
                <Link
                  href={breadcrumb.path}
                  className="group flex items-center gap-1.5 text-white/60 transition-colors duration-200 hover:text-white"
                >
                  {breadcrumb.path === '/' && (
                    <Home className="h-3.5 w-3.5 transition-transform duration-200 group-hover:scale-110 md:h-4 md:w-4" />
                  )}
                  <span>{breadcrumb.label}</span>
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  )
}
